import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Route, Clock, Building2, Plane, Factory, Hotel, MapPin, Users, Car, Bus, Crown, ShieldCheck, Sparkles, ArrowRight, Gauge } from 'lucide-react';
import { BrandHero, StatStrip, FeatureGrid, CtaBand, SectionHeader } from '@/components/brand/BrandKit';
import { SimpleLeadForm } from '@/components/forms/SimpleLeadForm';

const EVENTS_IMG = "/generated/staff-events.jpg";

const stats = [
  { value: "800+", label: "Buses & coaches" },
  { value: "5,000+", label: "Daily trips" },
  { value: "800+", label: "RTA-approved drivers" },
  { value: "7", label: "Emirates covered" },
];

const CAPACITIES = [
  { seats: "12", name: "Van", note: "Hiace or similar" },
  { seats: "20", name: "Sprinter", note: "Executive shuttle" },
  { seats: "22", name: "Coaster", note: "Team & route transport" },
  { seats: "25", name: "Bus", note: "Mid-capacity staff bus" },
  { seats: "35", name: "Coach", note: "Group & inter-site" },
  { seats: "50", name: "Luxury Coach", note: "Delegations & long trips" },
  { seats: "56", name: "Premium Staff Bus", note: "High-capacity staff transport" },
];

const STAFF_SERVICES = [
  { icon: Users, title: "Daily staff transportation", desc: "Scheduled employee movements on dedicated routes, sized to your headcount and shift pattern." },
  { icon: Hotel, title: "Accommodation transfers", desc: "Reliable transport between staff accommodation, sites and facilities across the Emirates." },
  { icon: MapPin, title: "Inter-site mobility", desc: "Structured inter-site and inter-property movement to keep multi-location operations connected." },
  { icon: Plane, title: "Airline & aviation staff", desc: "Crew and operational staff transport with punctuality and airside-aware coordination." },
  { icon: Factory, title: "Manufacturing & facilities", desc: "Shift-based transport for manufacturing plants, industrial zones and facilities-management teams." },
  { icon: Building2, title: "Hospitality staff transport", desc: "Dependable staff mobility for hotels, resorts and serviced-residence operations." },
];

const EVENT_SERVICES = [
  { icon: Crown, title: "VIP transfers & guest movements", desc: "Chauffeured VIP vehicles and coordinated guest movements that reflect your event's standards." },
  { icon: Car, title: "Self-drive & chauffeured", desc: "Both self-drive vehicles and driven mobility from one group — matched to each guest and role." },
  { icon: Bus, title: "Shuttle programmes", desc: "Continuous shuttle loops and scheduled coaches for attendees, crews and staff." },
  { icon: Plane, title: "Hotel & airport transfers", desc: "Seamless arrival and departure transfers between airports, hotels and the venue." },
  { icon: Users, title: "Staff & crew transportation", desc: "Reliable movement of event staff, crews and contractors throughout the programme." },
  { icon: Sparkles, title: "Mass ingress & egress", desc: "Experience managing large-scale arrival and departure sequencing for mass-transit event activity." },
];

function CapacityStrip() {
  return (
    <section data-testid="capacity-strip" className="bg-white py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader chip="The Fleet" title="Right-sized vehicles for every route." align="center"
          desc="A capacity for every group size, from a 12-seat van to a 56-seat premium staff bus. Vehicle availability is subject to route, schedule and demand." />
        <img src="/managed/coach-fleet-lineup.jpg" alt="Eurogulf Mobility Group coach fleet lined up" loading="lazy" className="mt-10 w-full h-56 sm:h-72 lg:h-80 rounded-2xl object-cover shadow-[0_24px_60px_rgba(0,0,0,0.12)]" />
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
          {CAPACITIES.map((c) => (
            <div key={c.seats + c.name} data-testid={`capacity-${c.seats}`} className="card-light p-5 text-center">
              <Bus className="w-6 h-6 text-[#EE5A01] mx-auto" />
              <p className="mt-3 font-heading font-black text-2xl text-[#121212] leading-none">{c.seats}</p>
              <p className="font-heading font-bold text-[10px] tracking-wider uppercase text-[#EE5A01] mt-1">seats</p>
              <p className="mt-2 font-heading font-bold text-sm text-[#121212]">{c.name}</p>
              <p className="mt-0.5 font-body text-xs text-[#666666]">{c.note}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center font-body text-xs text-[#888]">Vehicle types and capacities are indicative and subject to availability.</p>
      </div>
    </section>
  );
}

export default function StaffTransportPage() {
  useEffect(() => { document.title = "Staff & Events Transportation UAE | Eurogulf Mobility Group"; }, []);

  return (
    <div data-testid="staff-events-page">
      <BrandHero testid="staff-events-hero" chip="Managed Transportation" title="Staff & Events Transportation, run as one managed operation."
        lead="From daily staff movements to full event mobility, EGMG brings planning, dispatch, execution and support into one accountable operating structure — self-drive and driven, across all seven Emirates."
        image="/brand/coach-boarding-dubai.jpg" position="center 55%"
        ctas={[{ label: 'Request a Transport Plan', href: '#staff-events-enquiry', testid: 'staff-events-hero-cta' }, { label: 'Call 800 364', href: 'tel:800364', external: true, variant: 'outline' }]} />
      <StatStrip stats={stats} testid="staff-events-stats" />

      {/* Corporate & Staff Transportation */}
      <section data-testid="staff-section" className="bg-white py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeader chip="Corporate & Staff Transportation" title="Keeping your workforce moving."
                desc="We design staff transport around your operation — customised routes, shift patterns, employee locations, site access, passenger volumes and specific operational requirements — so attendance, punctuality and continuity are never left to chance." />
              <ul className="mt-6 grid grid-cols-2 gap-3">
                {["Customised routes", "Shift patterns", "Employee locations", "Site-access planning", "Passenger volumes", "Operational requirements"].map((t) => (
                  <li key={t} className="flex items-center gap-2 font-body text-sm text-[#121212]"><span className="h-1.5 w-1.5 rounded-full bg-[#EE5A01] flex-shrink-0" />{t}</li>
                ))}
              </ul>
            </div>
            <img src="/managed/coach-highway-dubai.jpg" alt="Eurogulf Mobility Group coach on a Dubai highway" loading="lazy" className="h-72 lg:h-80 w-full rounded-2xl object-cover shadow-[0_24px_60px_rgba(0,0,0,0.12)]" />
          </div>
        </div>
      </section>

      <FeatureGrid chip="What We Move" title="Staff mobility across every sector." bg="bg-[#FAFAFA]" items={STAFF_SERVICES} />

      {/* Telematics capability */}
      <section data-testid="staff-telematics" className="bg-white py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="relative overflow-hidden rounded-2xl border border-black/5 shadow-[0_24px_60px_rgba(0,0,0,0.12)]">
            <img src="/managed/telematics-control-room.jpg" alt="Eurogulf Mobility Group live fleet-tracking control room" loading="lazy" className="w-full h-64 sm:h-72 object-cover" />
          </div>
          <div className="flex flex-col sm:flex-row items-start gap-4 rounded-2xl border border-dashed border-[#EE5A01]/40 bg-[#FFF8F3] p-6">
            <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#EE5A01] text-white flex-shrink-0"><Gauge className="w-5 h-5" /></span>
            <div>
              <h3 className="font-heading font-bold text-base text-[#121212]">Data, telematics &amp; route optimisation</h3>
              <p className="mt-1 font-body text-sm text-[#666666] leading-relaxed">Live GPS tracking, movement monitoring and data-led route optimisation run from our 24/7 control room. Programme-level rollout is confirmed per contract, subject to operational approval.</p>
            </div>
          </div>
        </div>
      </section>

      <CapacityStrip />

      {/* Events Transportation */}
      <section data-testid="events-section" className="relative overflow-hidden bg-[#121212] text-white">
        <img src={EVENTS_IMG} alt="Delegates boarding Eurogulf shuttle coaches at a Dubai exhibition centre" loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <span className="chip-orange">Events Transportation</span>
          <h2 className="mt-5 font-heading font-black text-3xl sm:text-4xl lg:text-5xl leading-tight text-white max-w-3xl">Self-drive and driven mobility for events, from one group.</h2>
          <p className="mt-5 font-body text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            We deploy across the UAE with the experience to manage mass ingress and egress from major events and to support mobility for large-scale mass-transit activity — chauffeur vehicles, self-drive vehicles, coaches, buses and shuttle programmes, coordinated as a single operation.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {EVENT_SERVICES.map((s) => (
              <div key={s.title} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 hover:border-[#EE5A01]/60 hover:bg-white/[0.07] transition-colors">
                <span className="grid h-11 w-11 place-items-center rounded-lg bg-[#EE5A01] text-white"><s.icon className="w-5 h-5" /></span>
                <h3 className="mt-4 font-heading font-bold text-base text-white">{s.title}</h3>
                <p className="mt-2 font-body text-sm text-white/65 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <Link to="/royal-limousine" data-testid="events-to-coach" className="mt-8 inline-flex items-center gap-2 font-heading font-bold text-sm text-[#EE5A01] hover:gap-3 transition-all">Explore our bus & coach fleet <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* Managed transport lead form */}
      <section id="staff-events-enquiry" className="bg-[#FAFAFA] py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <SectionHeader chip="Managed Transport Enquiry" title="Let's plan your transport programme."
              desc="Share your routes, shift pattern, passenger volumes or event brief. Our managed-transport team will design a right-sized, single-partner solution and prepare your proposal." />
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-black/5 bg-white p-5">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-[#FFF4ED] text-[#EE5A01] flex-shrink-0"><ShieldCheck className="w-4 h-4" /></span>
              <p className="font-body text-xs text-[#666666] leading-relaxed">One accountable partner for planning, dispatch, execution and support. Commercial models (dedicated, route-based, per-trip or hybrid) are configured privately to your requirement.</p>
            </div>
          </div>
          <div className="lg:col-span-6">
            <SimpleLeadForm enquiryType="Managed Transport: Staff & Events" source="Staff & Events Transportation" title="Transport enquiry" intro="Tell us what you need to move and our team will respond within one working day." testid="staff-events-lead-form" />
          </div>
        </div>
      </section>

      <CtaBand title="One partner. Complete mobility solutions." desc="Staff, guests, crews or event crowds — we move them all, across all seven Emirates." ctas={[{ label: 'Contact Our Team', href: '/contact', testid: 'staff-events-cta' }, { label: 'View Bus & Coach', href: '/royal-limousine', variant: 'outline' }]} />
    </div>
  );
}
